import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../Auth/Auth';
import logger from '../../utils/logger';
import BreadCrumb from '../BreadCrumb';
import { Link, Navigate } from 'react-router-dom';
import {
  UserGroupIcon,
  ClockIcon,
  CalendarDaysIcon,
  DocumentCheckIcon,
  ChartBarIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import LeaveRequestApproval from './LeaveRequestApproval';
import LeavePolicyManager from './LeavePolicyManager';
import CreateLeaveEntry from './CreateLeaveEntry';
import LeaveCalendar from './LeaveCalendar';
import LeaveReports from './LeaveReports';
import EmployeePerformanceReview from './EmployeePerformanceReview';

const pages = [{ name: 'Human Resources', to: '/humanresources', component: Link, current: true }];

const HRDashboard = () => {
  const [activeComponent, setActiveComponent] = useState('leaveCalendar');
  const [pendingRequests, setPendingRequests] = useState(0);
  const [upcomingLeaves, setUpcomingLeaves] = useState(0);
  const { role } = useAuth();

  useEffect(() => {
    const fetchLeaveRequests = async () => {
      try {
        // Fetch pending leave requests
        const pendingQuery = query(collection(db, 'leaveRequests'), where('status', '==', 'Pending'));
        const pendingSnapshot = await getDocs(pendingQuery);
        setPendingRequests(pendingSnapshot.size);

        // Fetch upcoming leaves (e.g., leaves starting today or later)
        const today = new Date();
        const upcomingQuery = query(collection(db, 'leaveRequests'), where('startDate', '>=', today));
        const upcomingSnapshot = await getDocs(upcomingQuery);
        setUpcomingLeaves(upcomingSnapshot.size);
      } catch (error) {
        logger.error('Failed to fetch leave requests:', error);
      }
    };

    fetchLeaveRequests();
  }, []);

  const renderComponent = () => {
    switch (activeComponent) {
      case 'leaveCalendar':
        return <LeaveCalendar />;

      case 'leaveEntry':
        return <CreateLeaveEntry />;

      case 'leavePolicy':
        return <LeavePolicyManager />;

      case 'leaveApproval':
        return <LeaveRequestApproval />;

      case 'leaveReports':
        return <LeaveReports />;

      case 'performanceReviews':
        return <EmployeePerformanceReview />;

      default:
        return <LeaveCalendar />;
    }
  };

  if (role !== 'Finance' && role !== 'Admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-4">
      <BreadCrumb pages={pages} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg">
              <UserGroupIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">HR Manager Dashboard</h1>
              <p className="text-sm text-gray-600 mt-1">Manage employee leave requests, performance reviews, and HR policies</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-rose-500 hover:shadow-xl transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase">Pending Requests</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{pendingRequests}</p>
              </div>
              <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center">
                <ClockIcon className="w-6 h-6 text-rose-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-emerald-500 hover:shadow-xl transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase">Upcoming Leaves</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{upcomingLeaves}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <CalendarDaysIcon className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-blue-500 hover:shadow-xl transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase">Active Employees</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">-</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <UserGroupIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 border-l-4 border-purple-500 hover:shadow-xl transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-600 uppercase">Reviews Due</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">-</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <StarIcon className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-rose-500 to-pink-600 px-6 py-4">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveComponent('leaveCalendar')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  activeComponent === 'leaveCalendar'
                    ? 'bg-white text-rose-600 shadow-lg'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <CalendarDaysIcon className="w-4 h-4 inline-block mr-2" />
                Leave Calendar
              </button>

              <button
                onClick={() => setActiveComponent('performanceReviews')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  activeComponent === 'performanceReviews'
                    ? 'bg-white text-rose-600 shadow-lg'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <StarIcon className="w-4 h-4 inline-block mr-2" />
                Performance Reviews
              </button>

              <button
                onClick={() => setActiveComponent('leaveEntry')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  activeComponent === 'leaveEntry'
                    ? 'bg-white text-rose-600 shadow-lg'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <DocumentCheckIcon className="w-4 h-4 inline-block mr-2" />
                Create Leave Request
              </button>

              <button
                onClick={() => setActiveComponent('leavePolicy')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  activeComponent === 'leavePolicy'
                    ? 'bg-white text-rose-600 shadow-lg'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <UserGroupIcon className="w-4 h-4 inline-block mr-2" />
                Leave Policy
              </button>

              <button
                onClick={() => setActiveComponent('leaveApproval')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  activeComponent === 'leaveApproval'
                    ? 'bg-white text-rose-600 shadow-lg'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <ClockIcon className="w-4 h-4 inline-block mr-2" />
                Approval Requests
              </button>

              <button
                onClick={() => setActiveComponent('leaveReports')}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  activeComponent === 'leaveReports'
                    ? 'bg-white text-rose-600 shadow-lg'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <ChartBarIcon className="w-4 h-4 inline-block mr-2" />
                Leave Reports
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          {renderComponent()}
        </div>
      </div>
    </div>
  );
};

export default HRDashboard;
